import {attachmentAPI} from "../../services/api/attachmentAPI";
import _ from "lodash";
import Vue from "vue";

export const stashboardMediaModule = {
  state: () => ({
    maxImages: 5,

    loadingStashboardMedia: true,

    owner: null,
    ownerId: null,
    ownerType: null,
    stashboardCommunicationType: "mms",

    prevStashboardMedia: [],
    ownerStashboardMedia: [],

    focusedStashboardMedia: null,
    removedStashboardMedia: null
  }),
  getters: {
    maxImages: (state) => state.maxImages,
    loadingStashboardMedia: (state) => state.loadingStashboardMedia,
    stashboardOwner: (state) => state.owner,
    stashboardOwnerType: (state) => state.ownerType,
    stashboardOwnerId: (state) => state.ownerId,
    stashboardCommunicationType: (state) => state.stashboardCommunicationType,
    metaDisabledStashboardTabs: (state) =>  _.compact([
      state.owner.use_native_multiple_media ? null : "push"
    ]),
    stashboardMultipleEnabled: (state, getters) =>
      !getters.metaDisabledStashboardTabs.includes(state.stashboardCommunicationType),
    ownerStashboardMedia: (state) => state.ownerStashboardMedia,
    prevStashboardMedia: (state) => state.prevStashboardMedia,
    focusedStashboardMedia: (state) => state.focusedStashboardMedia,
    removedStashboardMedia: (state) => state.removedStashboardMedia,
    previewStashboardMedia: previewStashboardMedia,
    previewStashboardImages: previewStashboardImages,
    editStashboardImagesGrid: editStashboardImagesGrid,
    editStashboardVideo: editStashboardVideo
  },
  actions: {
    INITIALIZE_STASHBOARD_MEDIA: async function({ commit }, { owner, ownerType, ownerId, stashboardCommunicationType }) {
      commit("SET_LOADING_STASHBOARD_MEDIA", true)
      commit("SET_STASHBOARD_MEDIA_OWNER", {owner, ownerType, ownerId})
      commit("SET_STASHBOARD_COMMUNICATION_TYPE", stashboardCommunicationType)

      const stashboardParams = {
        owner_type: ownerType,
        owner_id: ownerId,
        labels: ["mms", "push"] // include all images from other campaigns, not just for the active comm type
      }
      let [ownerResult, prevResult] = await Promise.all([
        attachmentAPI.getStashboardMedia(stashboardParams),
        attachmentAPI.getStashboardMediaHistory(stashboardParams)
      ])

      commit("SET_PREV_STASHBOARD_MEDIA", prevResult.stashboard_media)
      commit("SET_OWNER_STASHBOARD_MEDIA", ownerResult.stashboard_media)

      commit("SET_LOADING_STASHBOARD_MEDIA", false)
    },
    BULK_SAVE_STASHBOARD_MEDIA: async function(
      { commit, state, getters },
      { stashboardMedia }
    ) {
      commit("SET_LOADING_STASHBOARD_MEDIA", true)

      const implicitRemovals = _.reject(state.ownerStashboardMedia, (sm) =>
        stashboardMedia.some((ism) => ism == sm)
      )
      const sorted = sortMedia(stashboardMedia).value()
      sorted.forEach((sm, idx) => {
        sm.position = idx + 1
      })
      const command = {
        owner_id: state.ownerId,
        owner_type: state.ownerType,
        stashboard_media: sorted
      }
      const {stashboard_media} = await attachmentAPI.bulkSaveStashboardMedia(command)

      commit("SET_OWNER_STASHBOARD_MEDIA", stashboard_media)
      commit("SET_LOADING_STASHBOARD_MEDIA", false)
      const removal = implicitRemovals[0];
      if (removal) commit("SET_REMOVED_STASHBOARD_MEDIA", removal);
      return stashboard_media
    },
    // replace an entire set for a scope (communication_type+media_type) with the passed in stashboardMedia
    BULK_SAVE_STASHBOARD_MEDIA_FOR_ONE_TYPE: async function(
      {dispatch, getters, state},
      {stashboardMedia, communication_type, media_type}
    ) {
      const myType = (sm) => sm.communication_type == communication_type && sm.media_type == media_type
      // ensure we don't accidentally update out-of-modal-scope other types, e.g. in the edit-stashboard-media-modal, which is currently focused on _just_ images
      const mySet = _.filter(stashboardMedia, myType)
      const otherTypesSet = _.reject(state.ownerStashboardMedia, (sm) => {
        return myType(sm)
          || (getters.metaDisabledStashboardTabs.includes(communication_type) && sm.communication_type == "universal") // wipe out ads if we're in "single-image mode" and submit the edit-stashboard-media modal when an ad is active
      })
      const media = [...mySet, ...otherTypesSet]
      return dispatch('BULK_SAVE_STASHBOARD_MEDIA', { stashboardMedia: media })
    },
    ADD_STASHBOARD_MEDIA: async function(
      { commit, dispatch, getters, state },
      { ad, copy_stashboard_media, file, communication_type, media_type, url }
    ) {
      commit("SET_LOADING_STASHBOARD_MEDIA", true)

      let attachment = null
      if (file) {
        // they become permanent on BULK_SAVE
        const permanent = false
        attachment = await attachmentAPI.create(state.ownerId, "mms", state.ownerType, file, permanent, media_type)
        if (attachment.errors) {
          commit("SET_LOADING_STASHBOARD_MEDIA", false)
          return attachment
        }
      }

      const command = {
        communication_type,
        media_type,
        ad_id: ad?.id,
        copy_stashboard_media_id: copy_stashboard_media?.id,
        created_at: new Date().toISOString(),
        attachment_id: attachment?.id,
        owner_id: state.ownerId,
        owner_type: state.ownerType,
        url,
        result_url: attachment?.url || copy_stashboard_media?.result_url || ad?.attachments[0]?.url
      }

      commit("SET_LOADING_STASHBOARD_MEDIA", false)
      return command
    },
    REMOVE_STASHBOARD_MEDIA: async function({ commit, state, getters }, { stashboardMedia }) {
      commit("SET_LOADING_STASHBOARD_MEDIA", true)

      await attachmentAPI.removeStashboardMedia(stashboardMedia.id)
      const idx = _.indexOf(state.ownerStashboardMedia, stashboardMedia)
      state.ownerStashboardMedia.splice(idx, 1)

      commit("SET_OWNER_STASHBOARD_MEDIA", state.ownerStashboardMedia)
      commit("SET_LOADING_STASHBOARD_MEDIA", false)
      commit("SET_REMOVED_STASHBOARD_MEDIA", stashboardMedia)
    },
    ADD_AD_STASHBOARD_MEDIA: async function(
      {commit, dispatch, state, getters},
      {ad}
    ) {
      const adSm = await dispatch('ADD_STASHBOARD_MEDIA', {
        ad: ad,
        communication_type: "universal",
        media_type: "image"
      })

      const otherTypesSet = _.reject(state.ownerStashboardMedia, (sm) =>
        (sm.communication_type == adSm.communication_type && sm.media_type == adSm.media_type) ||
          getters.metaDisabledStashboardTabs.includes(sm.communication_type)
      );
      const inclusive = [adSm, ...otherTypesSet];

      const sms = await dispatch('BULK_SAVE_STASHBOARD_MEDIA', {
        stashboardMedia: inclusive,
        communication_type: "universal",
        media_type: "image"
      })
      const ad_sm = _.find(sms, (sm) => sm.ad_id)
      commit("SET_FOCUSED_STASHBOARD_MEDIA", ad_sm)
    }
  },
  mutations: {
    SET_STASHBOARD_MEDIA_OWNER: function(state, {owner, ownerId, ownerType}) {
      state.owner = owner
      state.ownerId = ownerId
      state.ownerType = ownerType
    },
    SET_STASHBOARD_COMMUNICATION_TYPE: function(state, stashboardCommunicationType) {
      state.stashboardCommunicationType = stashboardCommunicationType
    },
    SET_LOADING_STASHBOARD_MEDIA: function(state, val) {
      state.loadingStashboardMedia = val
    },
    SET_PREV_STASHBOARD_MEDIA: function(state, prevStashboardMedia) {
      state.prevStashboardMedia = [...prevStashboardMedia]
    },
    SET_OWNER_STASHBOARD_MEDIA: function(state, ownerStashboardMedia) {
      state.ownerStashboardMedia = [...ownerStashboardMedia]
    },
    SET_FOCUSED_STASHBOARD_MEDIA: function(state, focusedStashboardMedia) {
      state.focusedStashboardMedia = focusedStashboardMedia
    },
    SET_REMOVED_STASHBOARD_MEDIA: function(state, removedStashboardMedia) {
      state.removedStashboardMedia = removedStashboardMedia
    }
  }
}

function sortMedia(media) {
  return _.chain(media).sortBy(
    (sm) => sm.ad_id ? 0 : 1,
    (sm) => sm.media_type == 'video' ? 0 : 1,
    (sm) => sm.position,
    (sm) => sm.created_at
  )
}

function previewDelimitMedia(state) {
  return sortMedia(state.ownerStashboardMedia)
    .filter((media) =>
      media.communication_type == 'universal' ||
        media.communication_type == state.stashboardCommunicationType
    )
}
function previewStashboardMedia(state) {
  return previewDelimitMedia(state).value()
}
function previewStashboardImages(state) {
  return previewDelimitMedia(state)
    .filter((media) => media.communication_type == state.stashboardCommunicationType)
    .value()
}
function editStashboardImagesGrid(state) {
  const mine = sm =>
    sm.communication_type == state.stashboardCommunicationType &&
      sm.media_type == "image"
  const sorted = sortMedia(state.ownerStashboardMedia)
  const siblings = sorted
    .reject(mine)
    .reject((sm) =>
      sm.communication_type == "universal" ||
        sm.media_type == "video"
    )
  const myImages = sorted.filter(mine)
  const ogId = (sm) => sm.original_stashboard_media_id
  const originalIds = myImages.map(ogId).value()
  const prevMedia = siblings
    .concat(state.prevStashboardMedia)
    .uniqBy(ogId)
    .reject((sm) => originalIds.includes(ogId(sm)))
    .value()
  return myImages.value().concat(_.take(prevMedia, 21 - myImages.value().length)) // total grid images capped at 21
}
function editStashboardVideo(state) {
  return sortMedia(state.ownerStashboardMedia)
    .filter(media => media.media_type == "video")
    .value()
}

export default {
  namespaced: true,
  state: stashboardMediaModule.state,
  getters: stashboardMediaModule.getters,
  actions: stashboardMediaModule.actions,
  mutations: stashboardMediaModule.mutations
}
